import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { 
  Users, Mail, Phone, Linkedin, Edit, Trash2, 
  UserPlus, Upload, X
} from 'lucide-react';

const TeamTab = ({ authAxios }) => {
  const [team, setTeam] = useState([]);
  const [teamSectionEnabled, setTeamSectionEnabled] = useState(true);
  const [editingTeamMember, setEditingTeamMember] = useState(null);
  const [newTeamMember, setNewTeamMember] = useState({ 
    name: '', role: '', email: '', phone: '', bio: '', photo_url: '', linkedin_url: '', sort_order: 0
  });
  const [showAddTeamForm, setShowAddTeamForm] = useState(false);
  const [uploadingTeamPhoto, setUploadingTeamPhoto] = useState(false);
  const teamPhotoInputRef = useRef(null);

  useEffect(() => {
    fetchTeamMembers();
    fetchTeamSectionEnabled();
  }, [authAxios]);

  const fetchTeamMembers = async () => {
    try {
      const response = await authAxios.get('/admin/team');
      setTeam(response.data);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    }
  };

  const fetchTeamSectionEnabled = async () => {
    try {
      const response = await authAxios.get('/admin/settings');
      if (response.data.team_section_enabled !== undefined) {
        setTeamSectionEnabled(response.data.team_section_enabled);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const handleToggleTeamSection = async () => {
    const newValue = !teamSectionEnabled;
    try {
      const currentSettings = await authAxios.get('/admin/settings');
      const settings = currentSettings.data;
      await authAxios.put('/admin/settings', {
        company_info: settings.company_info,
        contact_details: settings.contact_details,
        social_links: settings.social_links,
        team_section_enabled: newValue
      });
      setTeamSectionEnabled(newValue);
      toast.success(`Team section ${newValue ? 'enabled' : 'disabled'} successfully!`);
    } catch (error) {
      toast.error('Failed to toggle team section');
    }
  };

  const handleAddTeamMember = async () => {
    if (newTeamMember.name && newTeamMember.role) {
      try {
        const memberData = {
          name: newTeamMember.name, role: newTeamMember.role, email: newTeamMember.email,
          phone: newTeamMember.phone, bio: newTeamMember.bio, linkedin_url: newTeamMember.linkedin_url,
          sort_order: newTeamMember.sort_order
        };
        const response = await authAxios.post('/admin/team', memberData);
        let newMember = response.data.member;
        
        if (newTeamMember.photo_file) {
          const reader = new FileReader();
          reader.onload = async () => {
            const base64 = reader.result.split(',')[1];
            try {
              const photoResponse = await authAxios.post(`/admin/team/${newMember.id}/photo`, { image_base64: base64 });
              newMember.photo_url = photoResponse.data.photo_url;
              setTeam(prev => prev.map(m => m.id === newMember.id ? {...m, photo_url: photoResponse.data.photo_url} : m));
            } catch {
              toast.error('Member added but photo upload failed');
            }
          };
          reader.readAsDataURL(newTeamMember.photo_file);
        }
        
        setTeam(prev => [...prev, newMember].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
        setNewTeamMember({ name: '', role: '', email: '', phone: '', bio: '', photo_url: '', photo_file: null, linkedin_url: '', sort_order: 0 });
        setShowAddTeamForm(false);
        toast.success('Team member added successfully!');
      } catch (error) {
        toast.error('Failed to add team member');
      }
    }
  };

  const handleDeleteTeamMember = async (id) => {
    try {
      await authAxios.delete(`/admin/team/${id}`);
      setTeam(team.filter(m => m.id !== id));
      toast.success('Team member removed');
    } catch (error) {
      toast.error('Failed to delete team member');
    }
  };

  const handleToggleVisibility = async (memberId) => {
    try {
      const response = await authAxios.patch(`/admin/team/${memberId}/visibility`);
      setTeam(team.map(m => m.id === memberId ? {...m, visible: response.data.visible} : m));
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to toggle visibility');
    }
  };

  const handleUpdateTeamMember = async () => {
    if (editingTeamMember) {
      try {
        await authAxios.put(`/admin/team/${editingTeamMember.id}`, {
          name: editingTeamMember.name, role: editingTeamMember.role, email: editingTeamMember.email,
          phone: editingTeamMember.phone, bio: editingTeamMember.bio, photo_url: editingTeamMember.photo_url,
          linkedin_url: editingTeamMember.linkedin_url, sort_order: editingTeamMember.sort_order || 0
        });
        const updatedTeam = team.map(m => m.id === editingTeamMember.id ? editingTeamMember : m);
        setTeam(updatedTeam.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
        setEditingTeamMember(null);
        toast.success('Team member updated!');
      } catch (error) {
        toast.error('Failed to update team member');
      }
    }
  };

  const handleTeamPhotoUpload = async (e, memberId) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Image size must be less than 2MB'); return; }
    
    setUploadingTeamPhoto(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        const response = await authAxios.post(`/admin/team/${memberId}/photo`, { image_base64: base64 });
        if (editingTeamMember && editingTeamMember.id === memberId) {
          setEditingTeamMember({...editingTeamMember, photo_url: response.data.photo_url});
        }
        setTeam(team.map(m => m.id === memberId ? {...m, photo_url: response.data.photo_url} : m));
        toast.success('Photo uploaded successfully!');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingTeamPhoto(false);
      if (teamPhotoInputRef.current) teamPhotoInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6" data-testid="team-tab">
      {/* Section Visibility Toggle */}
      <div className="bg-[#111111] border border-white/5 p-4 flex items-center justify-between">
        <div>
          <h3 className="font-mono font-bold text-sm">Team Section Visibility</h3>
          <p className="text-white/50 text-xs mt-1">Show or hide the Management Team section on the website</p>
        </div>
        <button
          onClick={handleToggleTeamSection}
          data-testid="toggle-team-section"
          className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${
            teamSectionEnabled ? 'bg-[#D4AF37]' : 'bg-white/20'
          }`}
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
            teamSectionEnabled ? 'translate-x-8' : 'translate-x-1'
          }`} />
        </button>
      </div>

      <div className="flex justify-between items-center">
        <p className="text-white/50 text-sm">{team.length} team members</p>
        <button
          onClick={() => setShowAddTeamForm(true)}
          data-testid="add-team-member-btn"
          className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-mono text-sm hover:bg-[#D4AF37]/90 transition-colors"
        >
          <UserPlus className="w-4 h-4" />
          Add Member
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {team.map(member => (
          <motion.div
            key={member.id}
            className={`bg-[#111111] border p-6 transition-colors ${member.visible === false ? 'border-white/5 opacity-60' : 'border-white/5 hover:border-[#D4AF37]/30'}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-start justify-between mb-4">
              {member.photo_url ? (
                <img src={member.photo_url} alt={member.name} className="w-16 h-16 rounded-full object-cover border-2 border-[#D4AF37]/30" />
              ) : (
                <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                  <span className="font-mono text-[#D4AF37] font-bold text-xl">{member.name.charAt(0)}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-white/30 text-xs font-mono">#{member.sort_order || 0}</span>
                <button onClick={() => setEditingTeamMember({...member, sort_order: member.sort_order || 0})} className="p-2 text-white/50 hover:text-[#D4AF37] transition-colors">
                  <Edit className="w-4 h-4" />
                </button>
                <button onClick={() => handleDeleteTeamMember(member.id)} className="p-2 text-white/50 hover:text-red-400 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <h3 className={`font-mono font-bold text-lg ${member.visible === false ? 'text-white/50' : ''}`}>{member.name}</h3>
            <p className="text-[#D4AF37] text-sm font-mono">{member.role}</p>
            {member.bio && <p className="text-white/60 text-sm mt-2 line-clamp-2">{member.bio}</p>}
            <div className="mt-4 space-y-2 text-sm">
              {member.email && <p className="text-white/50 flex items-center gap-2"><Mail className="w-4 h-4" /> {member.email}</p>}
              {member.phone && <p className="text-white/50 flex items-center gap-2"><Phone className="w-4 h-4" /> {member.phone}</p>}
              {member.linkedin_url && (
                <a href={member.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-[#D4AF37] flex items-center gap-2">
                  <Linkedin className="w-4 h-4" /> LinkedIn Profile
                </a>
              )}
            </div>
            {/* Enable / Disable toggle */}
            <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
              <span className="text-white/40 text-xs font-mono">Visibility</span>
              <button
                onClick={() => handleToggleVisibility(member.id)}
                data-testid={`toggle-member-${member.id}`}
                className={`px-3 py-1 text-xs font-mono font-bold tracking-wide transition-all ${
                  member.visible !== false
                    ? 'bg-green-500/15 text-green-400 border border-green-500/30 hover:bg-green-500/25'
                    : 'bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25'
                }`}
              >
                {member.visible !== false ? 'ENABLED' : 'DISABLED'}
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Add Team Member Modal */}
      {showAddTeamForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <motion.div className="bg-[#111111] border border-white/10 w-full max-w-lg p-6 my-8" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-mono font-bold">Add Team Member</h3>
              <button onClick={() => setShowAddTeamForm(false)} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              <div className="flex items-center gap-4 p-4 bg-[#0A0A0A] border border-white/10">
                {newTeamMember.photo_url ? (
                  <img src={newTeamMember.photo_url} alt="Preview" className="w-20 h-20 rounded-full object-cover border-2 border-[#D4AF37]/30" />
                ) : (
                  <div className="w-20 h-20 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                    <span className="font-mono text-[#D4AF37] font-bold text-2xl">{newTeamMember.name ? newTeamMember.name.charAt(0).toUpperCase() : '?'}</span>
                  </div>
                )}
                <div className="flex-1">
                  <input type="file" accept="image/*" onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (event) => setNewTeamMember({...newTeamMember, photo_url: event.target.result, photo_file: file});
                      reader.readAsDataURL(file);
                    }
                  }} className="hidden" id="new-team-photo-upload" />
                  <label htmlFor="new-team-photo-upload" className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-white/70 font-mono text-sm hover:bg-white/5 transition-colors cursor-pointer">
                    <Upload className="w-4 h-4" />{newTeamMember.photo_url ? 'Change Photo' : 'Upload Photo'}
                  </label>
                  <p className="text-white/30 text-xs mt-2">Max 2MB, PNG/JPG</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/50 text-xs font-mono uppercase block mb-2">Full Name *</label>
                  <input type="text" placeholder="John Smith" value={newTeamMember.name} onChange={(e) => setNewTeamMember({...newTeamMember, name: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
                </div>
                <div>
                  <label className="text-white/50 text-xs font-mono uppercase block mb-2">Role/Position *</label>
                  <input type="text" placeholder="Managing Director" value={newTeamMember.role} onChange={(e) => setNewTeamMember({...newTeamMember, role: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/50 text-xs font-mono uppercase block mb-2">Email</label>
                  <input type="email" placeholder="john@company.com" value={newTeamMember.email} onChange={(e) => setNewTeamMember({...newTeamMember, email: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
                </div>
                <div>
                  <label className="text-white/50 text-xs font-mono uppercase block mb-2">Phone</label>
                  <input type="tel" placeholder="+44 7XXX XXXXXX" value={newTeamMember.phone} onChange={(e) => setNewTeamMember({...newTeamMember, phone: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
                </div>
              </div>
              <div>
                <label className="text-white/50 text-xs font-mono uppercase block mb-2">Bio</label>
                <textarea placeholder="Brief biography or description..." value={newTeamMember.bio} onChange={(e) => setNewTeamMember({...newTeamMember, bio: e.target.value})} rows={3} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/50 text-xs font-mono uppercase block mb-2">LinkedIn URL</label>
                  <input type="url" placeholder="https://linkedin.com/in/..." value={newTeamMember.linkedin_url} onChange={(e) => setNewTeamMember({...newTeamMember, linkedin_url: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
                </div>
                <div>
                  <label className="text-white/50 text-xs font-mono uppercase block mb-2">Sort Order</label>
                  <input type="number" placeholder="0" value={newTeamMember.sort_order} onChange={(e) => setNewTeamMember({...newTeamMember, sort_order: parseInt(e.target.value) || 0})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
                </div>
              </div>
              <button onClick={handleAddTeamMember} disabled={!newTeamMember.name || !newTeamMember.role} className="w-full bg-[#D4AF37] text-black py-3 font-mono text-sm hover:bg-[#D4AF37]/90 transition-colors disabled:opacity-50">
                Add Member
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Edit Team Member Modal */}
      {editingTeamMember && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <motion.div className="bg-[#111111] border border-white/10 w-full max-w-lg p-6 my-8" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-mono font-bold">Edit Team Member</h3>
              <button onClick={() => setEditingTeamMember(null)} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              <div className="flex items-center gap-4 p-4 bg-[#0A0A0A] border border-white/10">
                {editingTeamMember.photo_url ? (
                  <img src={editingTeamMember.photo_url} alt={editingTeamMember.name} className="w-20 h-20 rounded-full object-cover border-2 border-[#D4AF37]/30" />
                ) : (
                  <div className="w-20 h-20 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                    <span className="font-mono text-[#D4AF37] font-bold text-2xl">{editingTeamMember.name.charAt(0)}</span>
                  </div>
                )}
                <div className="flex-1">
                  <input ref={teamPhotoInputRef} type="file" accept="image/*" onChange={(e) => handleTeamPhotoUpload(e, editingTeamMember.id)} className="hidden" id="team-photo-upload" />
                  <label htmlFor="team-photo-upload" className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-white/70 font-mono text-sm hover:bg-white/5 transition-colors cursor-pointer">
                    <Upload className="w-4 h-4" />{uploadingTeamPhoto ? 'Uploading...' : 'Upload Photo'}
                  </label>
                  <p className="text-white/30 text-xs mt-2">Max 2MB, PNG/JPG</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/50 text-xs font-mono uppercase block mb-2">Full Name *</label>
                  <input type="text" value={editingTeamMember.name || ''} onChange={(e) => setEditingTeamMember({...editingTeamMember, name: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
                </div>
                <div>
                  <label className="text-white/50 text-xs font-mono uppercase block mb-2">Role/Position *</label>
                  <input type="text" value={editingTeamMember.role || ''} onChange={(e) => setEditingTeamMember({...editingTeamMember, role: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/50 text-xs font-mono uppercase block mb-2">Email</label>
                  <input type="email" value={editingTeamMember.email || ''} onChange={(e) => setEditingTeamMember({...editingTeamMember, email: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
                </div>
                <div>
                  <label className="text-white/50 text-xs font-mono uppercase block mb-2">Phone</label>
                  <input type="tel" value={editingTeamMember.phone || ''} onChange={(e) => setEditingTeamMember({...editingTeamMember, phone: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
                </div>
              </div>
              <div>
                <label className="text-white/50 text-xs font-mono uppercase block mb-2">Bio</label>
                <textarea value={editingTeamMember.bio || ''} onChange={(e) => setEditingTeamMember({...editingTeamMember, bio: e.target.value})} rows={3} placeholder="Brief biography or description..." className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/50 text-xs font-mono uppercase block mb-2">LinkedIn URL</label>
                  <input type="url" value={editingTeamMember.linkedin_url || ''} onChange={(e) => setEditingTeamMember({...editingTeamMember, linkedin_url: e.target.value})} placeholder="https://linkedin.com/in/..." className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
                </div>
                <div>
                  <label className="text-white/50 text-xs font-mono uppercase block mb-2">Sort Order</label>
                  <input type="number" value={editingTeamMember.sort_order || 0} onChange={(e) => setEditingTeamMember({...editingTeamMember, sort_order: parseInt(e.target.value) || 0})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
                </div>
              </div>
              <button onClick={handleUpdateTeamMember} className="w-full bg-[#D4AF37] text-black py-3 font-mono text-sm hover:bg-[#D4AF37]/90 transition-colors">
                Save Changes
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default TeamTab;
